import React, {useCallback, useEffect} from "react";
import {Button, Col, Container, Form, Row} from "react-bootstrap";
import styles from "../scss/Statistics.module.scss";
import {useParams, useSearchParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../app/store";
import {changeStatistics, getStatistics, StatisticsData} from "../app/slices/statistics";
import {Stopwatch, Trophy} from "react-bootstrap-icons";
import {ADMIN_LANG} from "../settings";
import GameCard from "./admin/gameView/GameCard";
import {formatDuration} from "./admin/utils";
import SelectChart from "./admin/questionView/SelectChart";
import SelectTable from "./admin/questionView/SelectTable";
import ConnectChart from "./admin/questionView/ConnectChart";
import ConnectTable from "./admin/questionView/ConnectTable";
import OrderChart from "./admin/questionView/OrderChart";
import OrderTable from "./admin/questionView/OrderTable";
import OpenTable from "./admin/questionView/OpenTable";
import GamePreLoader from "./games/GamePreLoader";
import PoweredBy from "../images/powered-by-scope.svg";
import NumberScaleChart from "./admin/questionView/NumberScaleChart";
import NumberScaleTable from "./admin/questionView/NumberScaleTable";
import AsyncTeamsSelect from "./admin/gameView/AsyncTeamsSelect";
import {PlainDetails} from "../types/admin";

const Statistics = () => {
    const {
        error, sections, questions,
        slides, pages, played,
        average_points, max_points, average_duration,
        statistics, name, statistics_pdf_url, theme, team_options, selected_teams,
        loaded
    } = useAppSelector((state) => state.statistics);

    const urlParams = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const params: any ={
        ...urlParams,
        teams: searchParams.getAll('teams'),
    }


    const {slug} = useParams();
    const dispatch = useAppDispatch();


    useEffect(() => {
        // NOTE: on React.StrictMode, this useEffect is called twice on dev mode
        if (slug) {
            dispatch(getStatistics({slug, ...params}));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug, JSON.stringify(params)]);

    const renderStatisticsSection = (stat: StatisticsData) => {
        switch (stat.type) {
            case "select":
                return (
                    <>
                        <SelectChart optionsData={stat.select_data}/>
                        <SelectTable maxPoints={max_points} optionsData={stat.select_data}/>
                    </>
                );
            case "connect":
                return (
                    <>
                        <ConnectChart optionsData={stat.connect_data}/>
                        <ConnectTable optionsData={stat.connect_data}/>
                    </>
                );
            case "order":
                return (
                    <>
                        <OrderChart optionsData={stat.order_data}/>
                        <OrderTable optionsData={stat.order_data}/>
                    </>
                );
            case "text":
                return (
                    <>
                        <OpenTable optionsData={stat.text_data}/>
                    </>
                );
            case "number_scale":
                return (
                    <>
                        <NumberScaleChart optionsData={stat.number_scale_data} npsScale={stat.nps_data}/>
                        <NumberScaleTable optionsData={stat.number_scale_data} npsScale={stat.nps_data}/>
                    </>
                );
            case "content":
                return null;
            default:
                return null;
        }
    };

    const renderPageBreak = (stat: StatisticsData) => {
        switch (stat.type) {
            case "select":
            case "connect":
            case "order":
            case "text":
            case "number_scale":
                return <div className={'print-break-before'}/>;
            default:
                return null;
        }
    };

    const handleOpenPdfUrl = () => {
        if (statistics_pdf_url) {
            window.open(statistics_pdf_url, '_blank');
        }
    }

     const handleTeamsChange = useCallback((selectedValues: PlainDetails[]) => {
        const newSearchParams = new URLSearchParams();

        Array.from(searchParams.keys()).forEach(key => {
            if (key !== 'teams') {
                const values = searchParams.getAll(key);
                values.forEach(value => {
                    newSearchParams.append(key, value);
                });
            }
        });

        selectedValues.forEach(team => {
            newSearchParams.append('teams', team.value.toString());
        });

        setSearchParams(newSearchParams);
        dispatch(changeStatistics({selected_teams: selectedValues}));
    }, [searchParams, setSearchParams, dispatch]);

    return (
        <>
        {theme && (
            <div className={styles.topBarStatistics} style={{ backgroundColor: theme.colors.top_bar_background }}>
                <div className={styles.topBarStatisticsContainer}>
                    <img src={theme.logo} alt='Logo' />
                </div>
            </div>
        )}
        <Container className={`${styles.containerStatistic} admin`}>
            {error && <div className="alert alert-danger">{error}</div>}

            <div>
                <img className={`${styles.poweredByStatistics}`} src={PoweredBy} alt='Powered by Scope'/>
            </div>

            <div className='d-flex justify-content-between align-items-center pb-3'>
                <h1>{name}</h1>
                <div className='d-flex align-items-center'>
                    <Button className={styles.hideForPrint} onClick={handleOpenPdfUrl} variant='primary'>{ADMIN_LANG.export_pdf}</Button>
                    <Form.Group controlId='teams' className={styles.teamSelect}>
                        <AsyncTeamsSelect selectedTeams={selected_teams} initialValues={team_options} onChange={handleTeamsChange}/>
                    </Form.Group>
                </div>
            </div>

            <Row className='pb-5'>
                <GameCard
                    icon={Trophy}
                    items={[
                        {label: ADMIN_LANG.sections, value: sections},
                        {label: ADMIN_LANG.questions, value: questions},
                        {label: ADMIN_LANG.slides, value: slides},
                        {label: ADMIN_LANG.pages, value: pages}
                    ]}
                    colProps={{xs: 12, lg: 6}}
                />
                <GameCard
                    icon={Stopwatch}
                    items={[
                        {label: ADMIN_LANG.games_played, value: played},
                        {label: ADMIN_LANG.average_points, value: average_points},
                        {label: ADMIN_LANG.max_points, value: max_points},
                        {label: ADMIN_LANG.average_duration, value: formatDuration(average_duration)}
                    ]}
                    colProps={{xs: 12, lg: 6}}
                />
            </Row>

            {statistics?.map((stat, index) => {
                return (
                    <Row key={`stat-section-${index}`} className="pb-5 print-avoid-break">
                        <Col xs={12}>
                            {renderPageBreak(stat)}
                            <h2><span className="badge bg-white text-dark me-1">#{index + 1}</span> {stat.title} <span
                                className="ms-2 badge bg-info">{stat.type_display}</span></h2>
                            <div className={styles.contentInfo}>
                                {stat.played !== null && (
                                    <p>{ADMIN_LANG.played}: {stat.played} {ADMIN_LANG.times}</p>
                                )}
                                {stat.average_points !== null && stat.max_points !== null && stat.max_points > 0 && (
                                    <p>{ADMIN_LANG.average_points}: {stat.average_points} / {stat.max_points}</p>
                                )}
                                <p>{ADMIN_LANG.average_duration}: {formatDuration(stat.average_duration)}</p>
                                {stat.type == "number_scale" && typeof stat.average_rating !== "undefined" && stat.average_rating !== null && (
                                    <p>{ADMIN_LANG.average}: {stat.average_rating}</p>
                                )}
                            </div>
                        </Col>
                        {renderStatisticsSection(stat)}
                    </Row>
                );
            })}
        <GamePreLoader isThemeLoaded={loaded}/>
        </Container>
        </>
    )
}

export default Statistics;